import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component, DestroyRef, HostBinding, inject, Inject, Input, OnInit, Optional,
  signal,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from '../../admin/services/admin.service';
import { UserService } from '../../services/user.service';
import { cloneDeep } from 'lodash-es';
import {  catchError, debounceTime, first, map } from 'rxjs/operators';

import { RoleService } from '../../role/role.service';
import { StatusesService } from '../../services/statuses.service';
import { WorkService } from '../work.service';
import { EMPTY, Observable } from 'rxjs';
import { toUnderscore } from '../../utils/string-utils';
import { IActionButton } from '../../models/work.model';
import { ChatService } from '../../chat/chat.service';
import { FirebaseService } from '../../services/firebase.service';
import { onChildChanged } from '@firebase/database';
import { AssignUserComponent } from '../assign-user/assign-user.component';
import { MatSnackBar } from '@angular/material/snack-bar';
@Component({
    selector: 'app-work-dialog',
    templateUrl: './work-dialog.component.html',
    styleUrls: ['./work-dialog.component.scss'],
    standalone: false
})
export class WorkDialogComponent implements OnInit {
  @Input() work: any = null;

  @Input() @HostBinding('class.client-mode') clientMode = false;

  @Input() @HostBinding('class.chat-mode') chatMode = false;

  @Input() workId = null;

  actionButtons = signal<IActionButton[]>(null);

  error = false;

  reloadTimeout = null;

  statuses = [];

  placeholders = [];

  filterCustom = [];

  setupSubs = [];

  updatingDocument = signal(false);

  chatService = inject(ChatService);

  firebase = inject(FirebaseService);

  destroyRef = inject(DestroyRef);

  loadingChat = signal<boolean>(false);

  private readonly dialog = inject(MatDialog);

  form = new UntypedFormGroup({
    pictures: new UntypedFormControl([]),
    sign_url: new UntypedFormControl(''),
    next_custom_work_id: new UntypedFormControl(null),
    custom_fields: new UntypedFormGroup({}),
  });


  private readonly snackBar = inject(MatSnackBar);

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data,
    private workService: WorkService,
    public roleService: RoleService,
    @Optional() private dialogRef: MatDialogRef<WorkDialogComponent>,
    private statusesService: StatusesService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    public appService: AdminService,
    private cdr: ChangeDetectorRef,
  ) { }

  get readOnly(): boolean {
    return this.work.completed_at || !this.work.user_id || (this.work.revisor_id && !this.isRevisor())
  }

  ngOnInit() {
    if ((this.data && this.data?.id) || this.workId) {
      this.loadWork(this.data?.id || this.workId);
    } else if (this.work) {
      this.data = {};
      this.data.type = 'WorkRequest';
      this.setupWork(this.work);
    } else {
      this.route.params.forEach((params) => {
        this.work = null;
        if (params.id) {
          this.loadWork(params.id);
        }
      });
    }
    this.statusesService.statuses('Status::Order').pipe(first()).subscribe((value) => {
      this.statuses = value;
    });
  }

  loadWork(id) {
    this.setupSubs.forEach((s) => s.unsubscribe());
    this.setupSubs = [];
    this.workService.get(id).toPromise().then((work) => {
      this.setupWork(work.body);
      this.cdr.detectChanges();
    }).catch((error:any) => {
      if (error.status === 403) {
        if (this.dialogRef instanceof MatDialogRef) {
          this.dialogRef.close();
        }else{
          this.router.navigate(['main', 'work']); 
        }
        
      }
    });
  }

  setupWork(work:any) {
    
    this.filterCustom = work.work?.custom_field_ids;
    this.form.patchValue(work);
    this.work = work;
    this.setupSubs.push(this.firebase.subscribeTo(`works/${work.document_type}-${work.document_id}`, (data) => {
      if(this.reloadTimeout || !this.destroyRef) clearTimeout(this.reloadTimeout);
      this.reloadTimeout = setTimeout(() => {
        this.loadWork(work.id);
      },1000)
    }, this.destroyRef, onChildChanged))
    this.getPlaceholders();
    this.cdr.detectChanges();
    this.form.get('custom_fields').valueChanges.pipe(debounceTime(1000)).subscribe((value) => {
      if(this.work){
        if (value && value !== this.work.custom_fields) {
          this.work.custom_fields = value;
  
          this.workService.update(this.work.id, { custom_fields: value }).toPromise().then(() => {
            this.work = cloneDeep(this.work);
          });
        }
      }
    });
  }

  toggle(item) {
    // CHECK LIST COMPLETED?
    this.workService.toggleItem(item.id, item).toPromise().catch((error) => {
      console.log(error);
    });
    this.getPlaceholders();
  }

  get hasPendingPlaceholder() {
    return this.placeholders.filter((i) => !i.url).length > 0;
  }

  getPendingImages() {
    return this.placeholders.filter((i) => !i.url).length + Math.max(0, this.work.work.min_photos - this.work.pictures.filter((p) => !p.placeholder_id).length);
  }

  getPlaceholders() {
    if (this.work.work) {
      const placeholders = [];
      if (this.work.check_list) {
        this.work.work_statuses.forEach((item, index) => {
          const image = this.work.pictures.find((p) => p.placeholder_id === item.id);
          if (item.value == 0 && this.work.check_list.danger_requires_photo) {
            placeholders.push({
              name: this.work.check_list.check_list_items[index].name, danger: true, id: item.id, url: image ? image.url : null,
            });
          } else if (item.value == 1 && this.work.check_list.warn_requires_photo) {
            placeholders.push({
              name: this.work.check_list.check_list_items[index].name, warn: true, id: item.id, url: image ? image.url : null,
            });
          }
        });
      }
      this.placeholders = placeholders;
    } else {
      this.placeholders = [];
    }
  }

  waitingRevision(): boolean {
    return this.work && this.work.work && !this.work.completed_at && this.work.work.require_revision && this.work.revisor_id;
  }

  isRevisor(): boolean {
    return (this.work.revisor_id && this.work.revisor_id === this.userService.user$.getValue().id);
  }

  saveImages(images:any[]) {
    this.cdr.markForCheck();
    const send = cloneDeep(images);
    send.forEach((i) => delete i.blob);
    this.workService.update(this.work.id, { pictures: send }).toPromise().then((response: HttpResponse<any>) => {
      const picsWithId = response.body.pictures;
      // ADD BLOB PREVIEW
      picsWithId.forEach((p) => {
        const imageWithBlob = images.find((i) => i.url === p.url);
        if (imageWithBlob) {
          p.blob = imageWithBlob.blob;
        }
      });
      this.work.pictures = picsWithId;
      this.work = cloneDeep(this.work);
      this.form.patchValue(this.work);
      this.cdr.detectChanges();
    }).catch((error) => {
      console.log(error);
    });
  }

  getCheckListName(id) {
    const index = this.work.check_list.check_list_items.findIndex((i) => i.id === id);
    if (index >= 0) {
      return this.work.check_list.check_list_items[index].name;
    }
    return 'Sin Nombre';
  }

  cloneData() {
    if (this.work && this.work.work_request) {
      this.work.custom_params = cloneDeep(this.work.work_request.custom_params);
      this.form.patchValue({ custom_params: this.work.work_request.custom_params });
    }
  }

  calcPoints(statuses, type) {
    let value = 0;
    if (type == 'check') {
      value = statuses.filter((s) => s.revision_completed).length / statuses.length * 100;
    }
    if (type == 'radio') {
      value = statuses.reduce((a, b) => a + b.revision_value, 0) / (statuses.length * 2) * 100;
    }
    // round
    return Math.round(value);
  }

  getGallery() {
    if (this.clientMode) return null;

    let parent = this.work.document;
    let pictures = [];

    while(parent) {
      pictures = pictures.concat(parent.order_attachments || []).concat(parent.pictures || []);
      parent = parent.document;
    }

    return pictures;
  }

  get canOpenDocument(): Observable<boolean> {
    if (!this.work?.document_type) return new Observable((observer) => observer.next(false));

    return this.roleService.can('read', toUnderscore(this.work.document_type)).pipe(
      map((canRead) => {
        return canRead && this.work.document;
      }),
    );
  }
  goBack() {
    if (this.dialogRef instanceof MatDialogRef) {
      this.dialogRef.close();
    } else if(this.work){
      const plural = this.work.document_type.toLowerCase() + 's';
      this.router.navigate(['main', plural, this.work.document_id]);
    } else{
      this.router.navigate(['main', 'works']);
    }
  }
  goToComments(){
    this.loadingChat.set(true);
    this.chatService.addChat(this.work.document_type, this.work.document_id).pipe(first()).subscribe({
      next: () => { 
        if (this.dialogRef instanceof MatDialogRef) {
          this.dialogRef.close();
        }
      },
      error: (error: any) => {
        console.error('Error adding chat', error);
      }
    });
  }

  canUpdateWork() {
    return this.roleService.can('update', 'work');
  }

  setUser(event, values) {
    event.preventDefault();
    event.stopPropagation();

    if (!values.roles_id) {
      return;
    }
    
    this.dialog.open(AssignUserComponent, { data: values.roles_id }).afterClosed().subscribe((user) => {
      if (!user) {
        return;
      }

      this.workService.update(values.id, { user_id: user.id }).pipe(
        catchError(() => {
          this.snackBar.open(
            'No se pudo asignar el usuario al trabajo',
            'Cerrar',
            { duration: 3000, panelClass: 'snackbar-error' }
          );
          return EMPTY;
        }),
      ).subscribe(() => {
        this.snackBar.open(
          'Usuario asignado al trabajo correctamente',
          'Cerrar',
          { duration: 3000, panelClass: 'snackbar-success' }
        );
        this.loadWork(this.data?.id || this.workId);
      })
    });
  }
}
